import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  getNotificationSettings,
  updateNotificationSettings,
} from "../api/notificationSettings";
import { useAppTranslation } from "../hooks/useAppTranslation";

export default function NotificationSettingsPage() {
  const { t } = useAppTranslation("common");
  const { selectedCompany } = useAuth();
  const companyId = selectedCompany;

  const [emailNotification, setEmailNotification] = useState(false);
  const [notifyUnconfirmedDocuments, setNotifyUnconfirmedDocuments] =
    useState(false);
  const [dayToNotify, setDayToNotify] = useState<number>(1);
  const [unprocessedDays, setUnprocessedDays] = useState<string>("3");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState<"success" | "error" | null>(null);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadSettings = async () => {
      try {
        setLoading(true);
        setSaveState(null);

        const data = await getNotificationSettings(companyId);

        if (cancelled) return;

        setEmailNotification(data.emailNotification ?? false);
        setNotifyUnconfirmedDocuments(data.notifyUnconfirmedDocuments ?? false);
        setDayToNotify(data.dayToNotifyUnconfirmedDocs ?? 1);
        setUnprocessedDays(String(data.unprocessedDaysToNotify ?? 3));
      } catch (error) {
        console.error("Nie udało się pobrać ustawień powiadomień", error);

        if (!cancelled) {
          setSaveState("error");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const parsedUnprocessedDays = Number(unprocessedDays);

  const hasUnprocessedDaysError =
    notifyUnconfirmedDocuments &&
    (!Number.isInteger(parsedUnprocessedDays) || parsedUnprocessedDays < 1);

  const handleSave = async () => {
    if (!companyId || saving) return;

    setSaveState(null);

    if (hasUnprocessedDaysError) {
      setSaveState("error");
      return;
    }

    try {
      setSaving(true);

      await updateNotificationSettings(companyId, {
        emailNotification,
        notifyUnconfirmedDocuments,
        dayToNotifyUnconfirmedDocs: dayToNotify,
        unprocessedDaysToNotify: notifyUnconfirmedDocuments
          ? parsedUnprocessedDays
          : Number(unprocessedDays) || 3,
      });

      setSaveState("success");
    } catch (error) {
      console.error("Nie udało się zapisać ustawień powiadomień", error);
      setSaveState("error");
    } finally {
      setSaving(false);
    }
  };

  if (!companyId) {
    return (
      <Box p={4}>
        <Typography variant="h6">
          {t("Wybierz firmę, aby zarządzać ustawieniami powiadomień")}
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: "calc(100vh - 64px)",
          backgroundColor: "background.default",
          py: 4,
        }}
      >
        <Box maxWidth={900} mx="auto">
          <Typography
            variant="h5"
            fontWeight={600}
            mb={1}
            sx={{ color: "#274156" }}
          >
            {t("Ustawienia powiadomień")}
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={4}>
            {t("Skonfiguruj sposób otrzymywania powiadomień dla wybranej firmy.")}
          </Typography>

          <Paper sx={{ p: 4, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              {t("Powiadomienia mailowe")}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={emailNotification}
                  disabled={saving}
                  onChange={(e) => setEmailNotification(e.target.checked)}
                />
              }
              label={t("Otrzymuj powiadomienia mailowe")}
            />
          </Paper>

          <Paper sx={{ p: 4 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              {t("Powiadomienia o zaległych dokumentach")}
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={3}>
              {t(
                "System może wysyłać przypomnienia o dokumentach, które nie zostały przetworzone w określonym czasie."
              )}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={notifyUnconfirmedDocuments}
                  disabled={saving}
                  onChange={(e) =>
                    setNotifyUnconfirmedDocuments(e.target.checked)
                  }
                />
              }
              label={t("Aktywuj powiadomienia")}
            />

            <Box mt={3} display="flex" gap={3} flexWrap="wrap">
              <Select
                value={dayToNotify}
                disabled={!notifyUnconfirmedDocuments || saving}
                onChange={(e) => setDayToNotify(Number(e.target.value))}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value={1}>{t("Poniedziałek")}</MenuItem>
                <MenuItem value={2}>{t("Wtorek")}</MenuItem>
                <MenuItem value={3}>{t("Środa")}</MenuItem>
                <MenuItem value={4}>{t("Czwartek")}</MenuItem>
                <MenuItem value={5}>{t("Piątek")}</MenuItem>
                <MenuItem value={6}>{t("Sobota")}</MenuItem>
                <MenuItem value={7}>{t("Niedziela")}</MenuItem>
              </Select>

              <TextField
                type="number"
                label={t("Dokumenty starsze niż (dni)")}
                disabled={!notifyUnconfirmedDocuments || saving}
                value={unprocessedDays}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setUnprocessedDays(e.target.value)}
                inputProps={{ min: 1 }}
                error={hasUnprocessedDaysError}
                helperText={
                  hasUnprocessedDaysError
                    ? t("Podaj liczbę dni większą lub równą 1")
                    : undefined
                }
              />
            </Box>
          </Paper>

          <Box mt={4}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "#1c6e8c",
                "&:hover": {
                  backgroundColor: "#155a70",
                },
              }}
              onClick={handleSave}
              disabled={saving || hasUnprocessedDaysError}
            >
              {saving ? t("Zapisywanie...") : t("Zapisz zmiany")}
            </Button>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(saveState)}
        autoHideDuration={3000}
        onClose={(_event, reason) => {
          if (reason === "clickaway") return;
          setSaveState(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={saveState === "success" ? "success" : "error"}
          variant="filled"
          sx={saveState !== "success" ? { backgroundColor: "#e63946" } : undefined}
        >
          {saveState === "success"
            ? t("Ustawienia zapisane pomyślnie")
            : t("Nie udało się zapisać ustawień")}
        </Alert>
      </Snackbar>
    </>
  );
}